export type TimestampFormat
  = | 'relative'
    | 'auto'
    | 'date'
    | 'date-time'
    | 'time'
    | 'system-date'
    | 'system-date-time'
    | 'system-time'

export type ResolvedTimestampFormat = Exclude<TimestampFormat, 'auto'>

export interface TimestampFormattingOptions {
  /** Reference clock used by relative and automatic formats. */
  now: Date
  /** BCP 47 locale used by Intl formatters. */
  locale?: string
  /** IANA timezone used for both localized and stable system fields. */
  timeZone?: string
  /** Includes a short timezone name on localized formats. */
  showTimeZone?: boolean
  /** Maximum relative age for automatic output, in seconds. */
  autoThreshold?: number
}

export interface TimestampLocaleOptions {
  locale?: string
  timeZone?: string
}

export interface RelativeTimeSelection {
  unit: Intl.RelativeTimeFormatUnit
  value: number
}

const SECOND_MS = 1000
const MINUTE_SECONDS = 60
const HOUR_SECONDS = 3600
const DAY_SECONDS = 86_400
const MONTH_SECONDS = 30 * DAY_SECONDS
const YEAR_SECONDS = 365 * DAY_SECONDS

/** Default automatic-format threshold: seven days. */
export const DEFAULT_AUTO_THRESHOLD = 7 * DAY_SECONDS

/** Small future deltas are assumed to be clock skew rather than future events. */
export const FUTURE_SKEW_TOLERANCE = 30

const formats = new Set<TimestampFormat>([
  'relative',
  'auto',
  'date',
  'date-time',
  'time',
  'system-date',
  'system-date-time',
  'system-time',
])

const isoPattern
  = /^([+-]\d{6}|\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(?:Z|[+-](\d{2}):(\d{2}))?)?$/i

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

function isValidIsoString(value: string): boolean {
  const match = isoPattern.exec(value)
  if (!match) {
    return false
  }

  const [, yearPart, monthPart, dayPart, hourPart, minutePart, secondPart, fractionPart, offsetHourPart, offsetMinutePart]
    = match
  const year = Number(yearPart)
  const month = Number(monthPart)
  const day = Number(dayPart)

  if (
    yearPart === '-000000'
    || month < 1
    || month > 12
    || day < 1
    || day > daysInMonth(year, month)
  ) {
    return false
  }

  if (hourPart !== undefined) {
    const hour = Number(hourPart)
    const minute = Number(minutePart)
    const second = Number(secondPart ?? 0)
    const isEndOfDay = hour === 24
      && minute === 0
      && second === 0
      && !/[1-9]/.test(fractionPart ?? '')
    if (hour > 24 || (hour === 24 && !isEndOfDay) || minute > 59 || second > 59) {
      return false
    }
  }

  if (
    offsetHourPart !== undefined
    && (Number(offsetHourPart) > 23 || Number(offsetMinutePart) > 59)
  ) {
    return false
  }

  return true
}

function validDate(date: Date): boolean {
  return Number.isFinite(date.getTime())
}

/**
 * Parses Timestamp's strict ISO calendar profile or finite Unix-second values.
 * Date-only input is UTC; date-times without an offset use the runtime timezone.
 */
export function parseTimestamp(value: string | number | undefined): Date | undefined {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return undefined
    }
    const date = new Date(value * SECOND_MS)
    return validDate(date) ? date : undefined
  }

  if (typeof value !== 'string' || !isValidIsoString(value)) {
    return undefined
  }

  const date = new Date(value)
  return validDate(date) ? date : undefined
}

function normalizeFormat(format: TimestampFormat): TimestampFormat {
  return formats.has(format) ? format : 'auto'
}

function normalizeThreshold(threshold = DEFAULT_AUTO_THRESHOLD): number {
  return Number.isFinite(threshold) && threshold >= 0 ? threshold : DEFAULT_AUTO_THRESHOLD
}

/** Resolves automatic output against an explicitly supplied reference clock. */
export function resolveTimestampFormat(
  date: Date,
  format: TimestampFormat,
  now: Date,
  autoThreshold = DEFAULT_AUTO_THRESHOLD,
): ResolvedTimestampFormat {
  const normalizedFormat = normalizeFormat(format)
  if (normalizedFormat !== 'auto') {
    return normalizedFormat
  }

  const thresholdMs = normalizeThreshold(autoThreshold) * SECOND_MS
  return Math.abs(date.getTime() - now.getTime()) <= thresholdMs ? 'relative' : 'date-time'
}

function relativeUnit(seconds: number): { unit: Intl.RelativeTimeFormatUnit, seconds: number } {
  if (seconds < MINUTE_SECONDS) {
    return { unit: 'second', seconds: 1 }
  }
  if (seconds < HOUR_SECONDS) {
    return { unit: 'minute', seconds: MINUTE_SECONDS }
  }
  if (seconds < DAY_SECONDS) {
    return { unit: 'hour', seconds: HOUR_SECONDS }
  }
  if (seconds < MONTH_SECONDS) {
    return { unit: 'day', seconds: DAY_SECONDS }
  }
  if (seconds < YEAR_SECONDS) {
    return { unit: 'month', seconds: MONTH_SECONDS }
  }
  return { unit: 'year', seconds: YEAR_SECONDS }
}

/** Selects a relative unit/value without rounding across its next boundary. */
export function selectRelativeTime(date: Date, now: Date): RelativeTimeSelection {
  const differenceMs = date.getTime() - now.getTime()
  if (differenceMs > 0 && differenceMs <= FUTURE_SKEW_TOLERANCE * SECOND_MS) {
    return { unit: 'second', value: 0 }
  }

  const absoluteSeconds = Math.floor(Math.abs(differenceMs) / SECOND_MS)
  const selected = relativeUnit(absoluteSeconds)
  const magnitude = Math.floor(absoluteSeconds / selected.seconds)

  return {
    unit: selected.unit,
    value: magnitude === 0 ? 0 : differenceMs < 0 ? -magnitude : magnitude,
  }
}

function dateTimeFormat(
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const attempts: Array<[string | undefined, Intl.DateTimeFormatOptions]> = [
    [locale, options],
    [undefined, options],
  ]

  if (options.timeZone !== undefined) {
    const { timeZone: _timeZone, ...withoutTimeZone } = options
    attempts.push([locale, withoutTimeZone], ['en-US', withoutTimeZone])
  }
  else {
    attempts.push(['en-US', options])
  }

  for (const [attemptLocale, attemptOptions] of attempts) {
    try {
      return new Intl.DateTimeFormat(attemptLocale, attemptOptions)
    }
    catch {
      // Invalid consumer locale/timezone options fall through to safe defaults.
    }
  }

  return new Intl.DateTimeFormat('en-US')
}

function relativeTimeFormat(locale: string | undefined): Intl.RelativeTimeFormat {
  try {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  }
  catch {
    return new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })
  }
}

function systemYear(year: string | number, era?: string): string {
  const calendarYear = Number(year)
  const astronomicalYear = era === 'BC' ? 1 - calendarYear : calendarYear
  if (astronomicalYear >= 0 && astronomicalYear <= 9999) {
    return String(astronomicalYear).padStart(4, '0')
  }
  const sign = astronomicalYear < 0 ? '-' : '+'
  return `${sign}${String(Math.abs(astronomicalYear)).padStart(6, '0')}`
}

function systemParts(date: Date, timeZone?: string): Record<string, string> {
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        era: 'short',
        hourCycle: 'h23',
        timeZone,
      })
      const parts = Object.fromEntries(
        formatter
          .formatToParts(date)
          .filter(part => part.type !== 'literal')
          .map(part => [part.type, part.value]),
      )
      return { ...parts, year: systemYear(parts.year, parts.era) }
    }
    catch {
      // Invalid timezones use the runtime's local fields instead.
    }
  }

  return {
    year: systemYear(date.getFullYear()),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    day: String(date.getDate()).padStart(2, '0'),
    hour: String(date.getHours()).padStart(2, '0'),
    minute: String(date.getMinutes()).padStart(2, '0'),
    second: String(date.getSeconds()).padStart(2, '0'),
  }
}

function hasNonPositiveCalendarYear(date: Date, timeZone?: string): boolean {
  if (timeZone !== undefined) {
    try {
      const era = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
        year: 'numeric',
        era: 'short',
        timeZone,
      }).formatToParts(date).find(part => part.type === 'era')?.value
      return era === 'BC'
    }
    catch {
      // Invalid timezones use the runtime's local calendar year below.
    }
  }

  return date.getFullYear() <= 0
}

function formatAbsoluteTimestamp(
  date: Date,
  format: Exclude<ResolvedTimestampFormat, 'relative'>,
  options: Omit<TimestampFormattingOptions, 'now' | 'autoThreshold'>,
): string {
  const { locale, showTimeZone = false, timeZone } = options
  if (format === 'date') {
    const era = hasNonPositiveCalendarYear(date, timeZone)
      ? { era: 'short' } as const
      : {}
    return dateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone,
      ...era,
      ...(showTimeZone ? { timeZoneName: 'short' } as const : {}),
    }).format(date)
  }

  if (format === 'date-time' || format === 'time') {
    const era = format === 'date-time' && hasNonPositiveCalendarYear(date, timeZone)
      ? { era: 'short' } as const
      : {}
    return dateTimeFormat(locale, {
      ...(format === 'date-time'
        ? { year: 'numeric', month: 'short', day: 'numeric' } as const
        : {}),
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
      ...era,
      ...(showTimeZone ? { timeZoneName: 'short' } as const : {}),
    }).format(date)
  }

  const parts = systemParts(date, timeZone)
  const dateText = `${parts.year}-${parts.month}-${parts.day}`
  const timeText = `${parts.hour}:${parts.minute}:${parts.second}`
  if (format === 'system-date') {
    return dateText
  }
  return format === 'system-time' ? timeText : `${dateText} ${timeText}`
}

/** Formats one valid timestamp using deterministic caller-supplied time. */
export function formatTimestamp(
  date: Date,
  format: TimestampFormat,
  options: TimestampFormattingOptions,
): string {
  if (!validDate(date) || !validDate(options.now)) {
    return ''
  }

  const resolved = resolveTimestampFormat(date, format, options.now, options.autoThreshold)
  if (resolved === 'relative') {
    const selection = selectRelativeTime(date, options.now)
    return relativeTimeFormat(options.locale).format(selection.value, selection.unit)
  }

  return formatAbsoluteTimestamp(date, resolved, options)
}

/** Formats the full absolute detail used as the relative timestamp's name. */
export function formatFullTimestamp(date: Date, options: TimestampLocaleOptions = {}): string {
  if (!validDate(date)) {
    return ''
  }
  return dateTimeFormat(options.locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZone: options.timeZone,
    timeZoneName: 'short',
    ...(hasNonPositiveCalendarYear(date, options.timeZone) ? { era: 'short' } as const : {}),
  }).format(date)
}

function nextRelativeBoundaryDelay(date: Date, now: Date): number {
  const differenceMs = date.getTime() - now.getTime()
  if (differenceMs > 0 && differenceMs <= FUTURE_SKEW_TOLERANCE * SECOND_MS) {
    return Math.max(1, Math.ceil(differenceMs + SECOND_MS))
  }

  const absoluteSeconds = Math.floor(Math.abs(differenceMs) / SECOND_MS)
  const { seconds: unitSeconds } = relativeUnit(absoluteSeconds)
  const unitMs = unitSeconds * SECOND_MS

  if (differenceMs > 0) {
    const decreasingBoundary = Math.floor(differenceMs % unitMs) + 1
    if (unitSeconds === 1) {
      const skewBoundary = Math.ceil(
        differenceMs - FUTURE_SKEW_TOLERANCE * SECOND_MS,
      )
      return Math.max(1, Math.min(decreasingBoundary, skewBoundary))
    }
    return Math.max(1, decreasingBoundary)
  }

  const ageMs = -differenceMs
  const nextValueBoundary = (Math.floor(ageMs / unitMs) + 1) * unitMs
  const nextUnitBoundary = unitSeconds === 1
    ? MINUTE_SECONDS * SECOND_MS
    : unitSeconds === MINUTE_SECONDS
      ? HOUR_SECONDS * SECOND_MS
      : unitSeconds === HOUR_SECONDS
        ? DAY_SECONDS * SECOND_MS
        : unitSeconds === DAY_SECONDS
          ? MONTH_SECONDS * SECOND_MS
          : unitSeconds === MONTH_SECONDS
            ? YEAR_SECONDS * SECOND_MS
            : Number.POSITIVE_INFINITY

  return Math.max(1, Math.ceil(Math.min(nextValueBoundary, nextUnitBoundary) - ageMs))
}

/** Returns the delay until output can next change, or undefined when stable. */
export function getNextTimestampUpdateDelay(
  date: Date,
  format: TimestampFormat,
  now: Date,
  autoThreshold = DEFAULT_AUTO_THRESHOLD,
): number | undefined {
  if (!validDate(date) || !validDate(now)) {
    return undefined
  }

  const normalizedFormat = normalizeFormat(format)
  if (normalizedFormat !== 'relative' && normalizedFormat !== 'auto') {
    return undefined
  }

  if (normalizedFormat === 'relative') {
    return nextRelativeBoundaryDelay(date, now)
  }

  const thresholdMs = normalizeThreshold(autoThreshold) * SECOND_MS
  const differenceMs = date.getTime() - now.getTime()
  if (differenceMs < -thresholdMs) {
    return undefined
  }
  if (differenceMs > thresholdMs) {
    return Math.max(1, Math.ceil(differenceMs - thresholdMs))
  }

  const thresholdExit = date.getTime() + thresholdMs - now.getTime() + 1
  return Math.min(nextRelativeBoundaryDelay(date, now), Math.max(1, thresholdExit))
}
